import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, ArchiveRestore, HandHeart, Loader2, MessageSquare, Search, SlidersHorizontal, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AssistanceOfferedCard } from "@/components/social/AssistanceOfferedCard";
import { AssistanceReceivedCard } from "@/components/social/AssistanceReceivedCard";

// Otter Assist — the Assistance Hub (OAD-107 … OAD-110).
//
// Two projections of the SAME canonical responder ledger, never a second responder system:
//   Assistance Offered  — where have I volunteered to help?
//   Assistance Received — who has offered to help me?
// Current / Archived is per-viewer organisation and is deliberately kept on its own level, below the
// Offered / Received selector, because the two answer different questions.

const OFFERED_SORTS = [
  { value: "offer_desc", label: "Most recent offer" },
  { value: "offer_asc", label: "Oldest offer" },
  { value: "updated", label: "Recently updated" },
  { value: "deadline", label: "Deadline" },
  { value: "urgency", label: "Urgency & deadline" },
];
const RECEIVED_SORTS = OFFERED_SORTS.map((s) =>
  s.value === "deadline" ? { value: "deadline", label: "Need deadline" } : s);

const OFFER_STATE_COPY = { offered: "Offered to help", withdrawn: "Withdrawn" };
const STATUS_COPY = {
  active: "Active", help_received: "Help received", closed: "Closed", expired: "Expired",
  cancelled: "Cancelled",
};
const URGENCY_COPY = {
  immediate: "Immediate", asap: "ASAP", soon: "Soon", within_days: "Within days",
  flexible: "Flexible",
};
const SENDER_STATUS_COPY = {
  new: "New", contacted: "Contacted", helpful: "Helpful", not_pursued: "Not pursued",
};

function SearchBox({ value, onChange, placeholder, testId }) {
  return (
    <div className="relative min-w-[180px] flex-1">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        aria-label={placeholder} className="h-8 pl-8 pr-7 text-[12px]" data-testid={testId} />
      {value && (
        <button type="button" onClick={() => onChange("")} aria-label="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          data-testid={`${testId}-clear`}>
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function FacetSelect({ label, options, value, onChange, copy, testId }) {
  const list = options || [];
  if (!list.length) return null;
  return (
    <div className="min-w-[150px]">
      <Label className="text-[11px] text-muted-foreground">{label}</Label>
      <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
        <SelectTrigger className="mt-1 h-8 text-[12px]" aria-label={label} data-testid={testId}>
          <SelectValue placeholder={`All ${label.toLowerCase()}`} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All {label.toLowerCase()}</SelectItem>
          {list.map((o) => (
            <SelectItem key={o} value={o} data-testid={`${testId}-${o}`}>
              {(copy && copy[o]) || o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function ViewToggle({ view, setView, prefix }) {
  return (
    <>
      <Button type="button" size="sm" variant={view === "current" ? "default" : "outline"}
        className="h-8 text-[11px]" onClick={() => setView("current")}
        aria-pressed={view === "current"} data-testid={`${prefix}-view-current`}>Current</Button>
      <Button type="button" size="sm" variant={view === "archived" ? "default" : "outline"}
        className="h-8 text-[11px]" onClick={() => setView("archived")}
        aria-pressed={view === "archived"} data-testid={`${prefix}-view-archived`}>Archived</Button>
    </>
  );
}

function AssistanceList({ direction }) {
  const offered = direction === "offered";
  const prefix = offered ? "assistance-offered" : "assistance-received";
  const qc = useQueryClient();
  const [view, setView] = useState("current");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("offer_desc");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    offer_state: "", assist_status: "", urgency: "", sender_status: "",
  });

  const params = useMemo(() => ({
    view, sort, limit: 25, q: q || undefined,
    offer_state: filters.offer_state || undefined,
    assist_status: filters.assist_status || undefined,
    urgency: filters.urgency || undefined,
    ...(offered ? {} : { sender_status: filters.sender_status || undefined }),
  }), [view, sort, q, filters, offered]);

  const { data, isLoading, isError } = useQuery({
    queryKey: [`oa-assistance-${direction}`, params],
    queryFn: async () =>
      (await api.get(`/otter-assist/assistance/${direction}`, { params })).data,
  });

  const archive = useMutation({
    mutationFn: async ({ actionId, archived }) =>
      (await api.post(`/otter-assist/assistance/${direction}/${actionId}/archive`,
        { archived })).data,
    onSuccess: (_res, vars) => {
      toast.success(vars.archived ? "Moved to Archived." : "Restored to Current.");
      qc.invalidateQueries({ queryKey: [`oa-assistance-${direction}`] });
      qc.invalidateQueries({ queryKey: ["oa-assistance-counts"] });
    },
    onError: () => toast.error("That didn't work — please try again."),
  });

  const items = data?.items || [];
  const facets = data?.facets || {};
  const activeFilters = Object.values(filters).filter(Boolean).length;
  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));
  const sorts = offered ? OFFERED_SORTS : RECEIVED_SORTS;

  return (
    <div data-testid={prefix}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <ViewToggle view={view} setView={setView} prefix={prefix} />
        <SearchBox value={q} onChange={setQ} testId={`${prefix}-search`}
          placeholder={offered ? "Search needs and people" : "Search people and needs"} />
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="h-8 w-[170px] text-[12px]" data-testid={`${prefix}-sort`}
            aria-label={offered ? "Sort assistance offered" : "Sort assistance received"}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {sorts.map((s) => (
              <SelectItem key={s.value} value={s.value} data-testid={`${prefix}-sort-${s.value}`}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button type="button" size="sm" variant={showFilters ? "default" : "outline"}
          className="h-8 text-[11px]" onClick={() => setShowFilters((v) => !v)}
          aria-expanded={showFilters} data-testid={`${prefix}-filters-toggle`}>
          <SlidersHorizontal className="mr-1.5 h-3.5 w-3.5" />
          Filters{activeFilters ? ` (${activeFilters})` : ""}
        </Button>
      </div>

      {showFilters && (
        <div className="mb-4 flex flex-wrap items-end gap-3 rounded-xl border border-border bg-secondary/30 p-3"
          data-testid={`${prefix}-filters`}>
          <FacetSelect label="Offer" options={facets.offer_states} copy={OFFER_STATE_COPY}
            value={filters.offer_state} onChange={(v) => setFilter("offer_state", v)}
            testId={`${prefix}-facet-offer-state`} />
          <FacetSelect label="Need status" options={facets.assist_statuses} copy={STATUS_COPY}
            value={filters.assist_status} onChange={(v) => setFilter("assist_status", v)}
            testId={`${prefix}-facet-assist-status`} />
          <FacetSelect label="Urgency" options={facets.urgencies} copy={URGENCY_COPY}
            value={filters.urgency} onChange={(v) => setFilter("urgency", v)}
            testId={`${prefix}-facet-urgency`} />
          {!offered && (
            <FacetSelect label="My status" options={facets.sender_statuses}
              copy={SENDER_STATUS_COPY} value={filters.sender_status}
              onChange={(v) => setFilter("sender_status", v)}
              testId={`${prefix}-facet-sender-status`} />
          )}
          {activeFilters > 0 && (
            <Button type="button" size="sm" variant="ghost" className="h-8 text-[11px]"
              onClick={() => setFilters({ offer_state: "", assist_status: "", urgency: "", sender_status: "" })}
              data-testid={`${prefix}-filters-clear`}>Clear filters</Button>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : isError ? (
        <div className="rounded-xl border border-dashed border-border py-14 text-center"
          data-testid={`${prefix}-error`}>
          <p className="text-sm text-foreground">Something went wrong. Please try again.</p>
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border py-14 text-center"
          data-testid={`${prefix}-empty`}>
          {offered
            ? <HandHeart className="mx-auto h-7 w-7 text-muted-foreground" />
            : <MessageSquare className="mx-auto h-7 w-7 text-muted-foreground" />}
          <p className="mt-3 text-sm font-[600] text-foreground">
            {q || activeFilters
              ? "Nothing matches that search"
              : view === "archived"
                ? "Nothing archived yet."
                : offered
                  ? "You haven't offered to help with any Otter Assist needs yet."
                  : "No one has offered to help with your Otter Assist needs yet."}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {q || activeFilters
              ? "Try a different word, or clear your filters."
              : offered
                ? "When you select I Can Help on a need, it will appear here."
                : "When someone selects I Can Help on one of your needs, they'll appear here."}
          </p>
        </div>
      ) : (
        <>
          <p className="mb-2 text-[11px] text-muted-foreground" data-testid={`${prefix}-count`}>
            {data.total === 1
              ? (offered ? "1 need" : "1 person")
              : `${data.total} ${offered ? "needs" : "people"}`}
          </p>
          <div className="space-y-3">
            {items.map((item) => (offered ? (
              <AssistanceOfferedCard key={item.action_id} item={item} view={view}
                onArchive={(archived) =>
                  archive.mutate({ actionId: item.action_id, archived })} />
            ) : (
              <AssistanceReceivedCard key={item.action_id} item={item} view={view}
                senderStatuses={data.sender_statuses || []}
                onArchive={(archived) =>
                  archive.mutate({ actionId: item.action_id, archived })} />
            )))}
          </div>
        </>
      )}
    </div>
  );
}

export function AssistanceHub() {
  const [searchParams, setSearchParams] = useSearchParams();
  // OAD-114 — "Return to Otter Assist" can restore Offered / Received, so the direction lives in
  // the URL. Anything other than the two known values falls back to Offered.
  const direction = searchParams.get("direction") === "received" ? "received" : "offered";
  const setDirection = (value) => {
    const next = new URLSearchParams(searchParams);
    next.set("direction", value);
    setSearchParams(next, { replace: true });
  };
  const { data: counts } = useQuery({
    queryKey: ["oa-assistance-counts"],
    queryFn: async () => (await api.get("/otter-assist/assistance/counts")).data,
  });

  return (
    <div data-testid="otter-assist-assistance">
      <Tabs value={direction} onValueChange={setDirection}>
        <TabsList className="mb-4" data-testid="assistance-direction-tabs">
          <TabsTrigger value="offered" data-testid="assistance-tab-offered">
            Assistance Offered
            {counts?.offered_current ? (
              <span className="ml-1.5 text-[10px] text-muted-foreground">
                ({counts.offered_current})
              </span>
            ) : null}
          </TabsTrigger>
          <TabsTrigger value="received" data-testid="assistance-tab-received">
            Assistance Received
            {counts?.received_current ? (
              <span className="ml-1.5 text-[10px] text-muted-foreground">
                ({counts.received_current})
              </span>
            ) : null}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="offered"><AssistanceList direction="offered" /></TabsContent>
        <TabsContent value="received"><AssistanceList direction="received" /></TabsContent>
      </Tabs>
    </div>
  );
}

export default AssistanceHub;
